import { useEffect, useState } from 'react';
import { Chart as ChartJS } from 'chart.js';
import axios from 'axios';
import Debug from 'debug';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from '@mui/material';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { useDispatch } from 'react-redux';
import ChartPie from '../../../../components/charts/ChartPie';
import { register } from '../../../../components/charts/charts-data-config';
import { AuthHeader } from '../../../../utils/helpers/auth-headers';
import { AlertSeverity } from '../../../../utils/enum/Alerts';
import './RealTimeStatistics.scss';
import { setAlertData } from '../../../../stores/alertStore';

const debug = Debug('monitoring:RealTimeStatisticsPage');

export default function RealTimeStatistics() {
  ChartJS.register(register);

  const { t } = useTranslation();
  const paramsRoute = useParams();
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const i18nLang = i18next.language;

  const [videoQualityChartData, setVideoQualityChartData] = useState({});
  const [protocolsChartData, setProtocolsChartData] = useState({});
  const [clientsPerBrowserChartData, setClientsPerBrowserChartData] = useState({});
  const [versionsPerBrowserChartData, setVersionsPerBrowserChartData] = useState({});

  const [instanceStats, setInstanceStats] = useState<any>();

  const setCharts = () => {
    if (instanceStats) {
      const protocolsData = instanceStats.protocol;
      const labelsProtocol = Object.entries(protocolsData).map(([key, value]) => `${key} (${value})`);
      const dataProtocol = Object.entries(protocolsData).map(([key, value]) => value as number);
      setProtocolsChartData({
        displaySubtitle: false,
        displayTitle: true,
        title: t('RealTimeStatistics.chartTitle.protocols'),
        data: dataProtocol,
        labels: labelsProtocol,
      });
      const browserData = instanceStats.browser;
      const labelsBrowser = Object.entries(browserData).map(([key, value]) => `${key} (${value})`);
      const dataBrowser = Object.entries(browserData).map(([key, value]) => value as number);
      setClientsPerBrowserChartData({
        displaySubtitle: false,
        displayTitle: true,
        title: t('RealTimeStatistics.chartTitle.clientPerBrowser'),
        data: dataBrowser,
        labels: labelsBrowser,
      });
      const browserVersionData = instanceStats.browserVersion;
      const labelsBrowserVersion = Object.entries(browserVersionData).map(([key, value]) => `${key} (${value})`);
      const dataBrowserVersion = Object.entries(browserVersionData).map(([key, value]) => value as number);
      setVersionsPerBrowserChartData({
        displaySubtitle: false,
        displayTitle: true,
        title: t('RealTimeStatistics.chartTitle.versionPerBrowser'),
        data: dataBrowserVersion,
        labels: labelsBrowserVersion,
        navigatorVersion: true
      });
      const qualityData = instanceStats.quality;
      const labelsQuality = Object.entries(qualityData).map(([key, value]) => `${key} (${value})`);
      debug(labelsQuality);
      const dataQuality = Object.entries(qualityData).map(([key, value]) => value as number);
      setVideoQualityChartData({
        displaySubtitle: true,
        displayTitle: true,
        subtitle: t('RealTimeStatistics.chartSubtitle.videoQuality', { instanceStats }),
        title: t('RealTimeStatistics.chartTitle.videoQuality'),
        data: dataQuality,
        labels: labelsQuality,
        quality: true
      });
    }
  };

  const getStatistics = () => {
    axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${paramsRoute.instanceId}/liveStats`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => setInstanceStats(response.data.data))
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          dispatch(setAlertData({
            message: err.data.message,
            severity: AlertSeverity.ERROR,
            open: true,
          }));
        }
        console.error(error);
      });
  };

  useEffect(() => {
    getStatistics();
  }, [navigate]);

  useEffect(() => {
    setCharts();
  }, [instanceStats, i18nLang]);

  return (
    <div className="category-container">
      <Card sx={{ marginBottom: 2 }}>
        <div className="global-stats">
          <div><strong>{ t('RealTimeStatistics.global.meetingCount') }:&nbsp;</strong>{instanceStats?.meetingCount}</div>
          <div><strong>{ t('RealTimeStatistics.global.participantCount') }:&nbsp;</strong>{instanceStats?.participantCount}</div>
          <div><strong>{ t('RealTimeStatistics.global.maxMeetingParticipants') }:&nbsp;</strong>{instanceStats?.maxMeetingParticipants}</div>
        </div>
      </Card>

      <div className="charts">
        <ChartPie chartData={videoQualityChartData} />
        <ChartPie chartData={protocolsChartData} />
        <ChartPie chartData={clientsPerBrowserChartData} />
        <ChartPie chartData={versionsPerBrowserChartData} />
      </div>
    </div>
  );
}
